import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/dahfonrp14a1h3mr6l20";

type FormStatus = "idle" | "loading" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function SubmitForm() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // 反垃圾：蜜罐字段被填写则静默按成功处理
    const decoy = String(formData.get("website_alt") ?? "").trim();
    if (decoy) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("loading");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") payload.append(key, value);
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试");
      }
    } catch (error) {
      setStatus("error");
      setFormError(error instanceof Error ? error.message : "网络异常，请稍后再试");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-8 text-center">
        <span className="mx-auto w-12 h-12 rounded-full bg-secondary-500 text-background-50 flex items-center justify-center text-2xl">
          <i className="ri-check-line"></i>
        </span>
        <h3 className="mt-4 text-lg text-secondary-900">收到啦，谢谢你！</h3>
        <p className="mt-2 text-sm text-secondary-800 leading-relaxed">
          我们会在三个工作日内查看你的投稿或留言，被采纳的内容会标注作者署名。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-secondary-500 text-background-50 text-sm hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="chigua-submit-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="submit-name" className="block text-sm text-foreground-800 mb-2">
            怎么称呼你
          </label>
          <input
            id="submit-name"
            name="name"
            type="text"
            required
            maxLength={40}
            placeholder="昵称或网名都可以"
            className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400"
          />
        </div>
        <div>
          <label htmlFor="submit-email" className="block text-sm text-foreground-800 mb-2">
            联系邮箱
          </label>
          <input
            id="submit-email"
            name="email"
            type="email"
            required
            maxLength={80}
            placeholder="用于回复你的投稿"
            className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="submit-topic" className="block text-sm text-foreground-800 mb-2">
          投稿类型
        </label>
        <select
          id="submit-topic"
          name="topic"
          required
          defaultValue="表情包"
          className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer"
        >
          <option value="表情包">表情包</option>
          <option value="段子">段子</option>
          <option value="神回复">神回复</option>
          <option value="梗图">梗图</option>
          <option value="二创视频">二创视频</option>
          <option value="内容反馈">内容反馈 / 侵权说明</option>
          <option value="合作">商务合作</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="submit-content" className="block text-sm text-foreground-800 mb-2">
          想说的话
        </label>
        <textarea
          id="submit-content"
          name="content"
          required
          maxLength={500}
          rows={5}
          placeholder="简单描述一下你的内容或建议，不超过 500 字"
          className="w-full px-3 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none placeholder:text-foreground-400 leading-relaxed"
        />
        <p className="mt-2 text-xs text-foreground-400">最多 500 字，我们会认真读完每一条。</p>
      </div>

      <div className="field-supplemental" aria-hidden="true">
        <label htmlFor="submit-website-alt">请勿填写此字段</label>
        <input
          id="submit-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" ? (
        <p className="mt-4 rounded-md bg-primary-50 border border-primary-200 px-3 py-2.5 text-sm text-primary-700">
          {formError || "提交失败，请稍后再试"}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={status === "loading"}
        className="mt-5 inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
        </span>
        {status === "loading" ? "正在提交" : "提交投稿"}
      </button>
    </form>
  );
}